export const OKIRU_PRESENTATION_TEMPLATES = {
  "okiru-light": {
    id: "okiru-light",
    name: "Okiru Light",
    description:
      "Warm sand canvas with charcoal type and the complete Okiru colour palette.",
    theme: {
      background: "#F4F2ED",
      backgroundAlt: "#E9E6DF",
      card: "#FBFAF7",
      text: "#111419",
      textSoft: "#4F545B",
      textFaint: "#7A7F86",
      line: "#D6D1C8",
      accent: "#F26A21",
      accentDeep: "#C84B0A",
      accentSoft: "rgba(242,106,33,0.10)",
      brandCyan: "#10B9C7",
      brandBlue: "#1557A5",
      brandMagenta: "#A80F8B",
      brandOrange: "#F26A21",
      good: "#16825D",
      goodSoft: "rgba(22,130,93,0.11)",
      heroBackground: "linear-gradient(135deg, #191C20 0%, #0B0E12 62%, #05070A 100%)",
      sectionBackground: "linear-gradient(120deg, #101419 0%, #24272B 56%, #090C10 100%)",
      displayFont: "Roboto, Arial, sans-serif",
      bodyFont: "Roboto, Arial, sans-serif",
    },
  },
  "okiru-dark": {
    id: "okiru-dark",
    name: "Okiru Dark",
    description:
      "Premium charcoal canvas with crisp white type and luminous Okiru brand colours.",
    theme: {
      background: "#0B0E12",
      backgroundAlt: "#15191E",
      card: "#1A1E23",
      text: "#F8FAFC",
      textSoft: "#C3CBD6",
      textFaint: "#8993A2",
      line: "#34383E",
      accent: "#F26A21",
      accentDeep: "#FF9A54",
      accentSoft: "rgba(242,106,33,0.14)",
      brandCyan: "#10B9C7",
      brandBlue: "#2F73EE",
      brandMagenta: "#C52BAC",
      brandOrange: "#FF7A2D",
      good: "#10E8A0",
      goodSoft: "rgba(16,232,160,0.13)",
      heroBackground: "linear-gradient(135deg, #191C20 0%, #0B0E12 62%, #05070A 100%)",
      sectionBackground: "linear-gradient(120deg, #101419 0%, #24272B 56%, #090C10 100%)",
      displayFont: "Roboto, Arial, sans-serif",
      bodyFont: "Roboto, Arial, sans-serif",
    },
  },
};

const ILLUSTRATION_ROOT = "/presentation-assets/okiru-template/illustrations";

export const OKIRU_TEMPLATE_ASSETS = [
  { id: "team-collaboration", name: "Team collaboration", category: "People", alt: "A team collaborating around a shared workstation", recommendedLayouts: ["intro", "lead", "about"] },
  { id: "workshop-facilitator", name: "Workshop facilitator", category: "People", alt: "A facilitator presenting to two workshop participants", recommendedLayouts: ["intro", "lead"] },
  { id: "thinking-learner", name: "Thinking learner", category: "People", alt: "A learner considering several options", recommendedLayouts: ["lead", "keyterm"] },
  { id: "conversation-coaching", name: "Coaching conversation", category: "People", alt: "Two colleagues having a constructive coaching conversation", recommendedLayouts: ["lead", "cards", "keyterm"] },
  { id: "problem-solving", name: "Problem solving", category: "People", alt: "A professional untangling a difficult problem", recommendedLayouts: ["lead", "cards", "keyterm"] },
  { id: "action-communication", name: "Communication in action", category: "People", alt: "A team communicating through a large announcement graphic", recommendedLayouts: ["intro", "lead", "media"] },
  { id: "take-action", name: "Take action", category: "People", alt: "A professional surrounded by action and outcome symbols", recommendedLayouts: ["lead", "media"] },
  { id: "icon-empathy", name: "Empathy", category: "Icons", alt: "A hand supporting a heart", lightOnly: true, recommendedLayouts: ["cards", "lead"] },
  { id: "icon-professionalism", name: "Professionalism", category: "Icons", alt: "A professional team aligned around a target", lightOnly: true, recommendedLayouts: ["cards", "lead"] },
  { id: "icon-responsiveness", name: "Responsiveness", category: "Icons", alt: "Messages moving quickly in both directions", lightOnly: true, recommendedLayouts: ["cards", "lead"] },
  { id: "icon-ownership", name: "Ownership", category: "Icons", alt: "A key unlocking an idea", lightOnly: true, recommendedLayouts: ["cards", "lead"] },
  { id: "icon-consistency", name: "Consistency", category: "Icons", alt: "A gear and clock representing consistent delivery", lightOnly: true, recommendedLayouts: ["cards", "lead"] },
].map((asset) => ({
  ...asset,
  light: `${ILLUSTRATION_ROOT}/${asset.id}-light.svg`,
  dark: asset.lightOnly ? `${ILLUSTRATION_ROOT}/${asset.id}-light.svg` : `${ILLUSTRATION_ROOT}/${asset.id}-dark.svg`,
}));

export function resolveOkiruTemplateAsset(assetId, tone = "light") {
  const asset = OKIRU_TEMPLATE_ASSETS.find((item) => item.id === assetId);
  if (!asset) return null;
  return { ...asset, src: tone === "dark" ? asset.dark : asset.light };
}

export const OKIRU_DECK_BLUEPRINT = [
  {
    layout: "title",
    animation: "fade",
    content: {
      kicker: "Okiru Learn",
      title: "Presentation title",
      lead: "A clear promise of what the audience will understand or be able to do.",
      meta: [],
    },
  },
  {
    layout: "statement",
    animation: "rise",
    content: {
      text: "State the single outcome this presentation is designed to create.",
      footnote: "Keep this specific, useful and audience-facing.",
    },
  },
  {
    layout: "agenda",
    animation: "fade",
    content: {
      kicker: "The journey",
      title: "How we will get there",
      columns: [
        [
          { time: "01", label: "Context and purpose" },
          { time: "02", label: "Core ideas" },
        ],
        [
          { time: "03", label: "Apply it" },
          { time: "04", label: "Next steps" },
        ],
      ],
    },
  },
  {
    layout: "intro",
    animation: "slide-left",
    content: {
      module: 1,
      kicker: "Section 01",
      title: "Set the context",
      lead: "Explain why this matters now.",
      outcome: "The audience understands the problem or opportunity.",
      templateAsset: "workshop-facilitator",
      imageChrome: "none",
    },
  },
  {
    layout: "lead",
    animation: "rise",
    content: {
      kicker: "Core idea",
      title: "Make one clear point",
      lead: "Lead with the meaning, not a list of information.",
      body: "Use concise evidence, an example or a visual to support the point.",
      templateAsset: "thinking-learner",
      imageChrome: "none",
    },
  },
  {
    layout: "cards",
    animation: "rise",
    content: {
      kicker: "Evidence",
      title: "Show what this means in practice",
      lead: "",
      columns: 3,
      cards: [
        { title: "Insight", description: "Add a grounded example." },
        { title: "Implication", description: "Explain why it matters." },
        { title: "Action", description: "Show what to do next." },
      ],
    },
  },
  {
    layout: "exercise",
    animation: "rise",
    content: {
      kicker: "Apply it",
      title: "Turn the idea into action",
      meta: "Activity",
      steps: [
        { title: "Choose", detail: "Select a relevant real-world example." },
        { title: "Apply", detail: "Use the method on your example." },
        {
          title: "Review",
          detail: "Check the result against the intended outcome.",
        },
      ],
      checkFor: "The result is useful, specific and ready to use.",
    },
  },
  {
    layout: "outcome",
    animation: "fade",
    content: {
      kicker: "Next step",
      title: "Leave with a clear action",
      checks: [
        "The key idea is understood",
        "The next action is specific",
        "Ownership and timing are clear",
      ],
    },
  },
];

export function applyOkiruTemplate(manifest, templateId = "okiru-light") {
  const preset =
    OKIRU_PRESENTATION_TEMPLATES[templateId] ||
    OKIRU_PRESENTATION_TEMPLATES["okiru-light"];
  return {
    ...manifest,
    templateId: preset.id,
    templateVersion: 3,
    logo: "/okiru-logo.png",
    logoDark: "/okiru-logo-white.png",
    footerLogoBlack: "/okiru-footer-black.png",
    footerLogoWhite: "/okiru-footer-white.png",
    clientLogo: manifest.clientLogo || "",
    theme: { ...preset.theme },
  };
}

export function ensureOkiruTemplate(manifest = {}) {
  const templateId = OKIRU_PRESENTATION_TEMPLATES[manifest.templateId]
    ? manifest.templateId
    : "okiru-light";
  const base = applyOkiruTemplate({}, templateId);
  const isLegacyTheme = (manifest.templateVersion || 1) < 2;
  return {
    ...base,
    ...manifest,
    templateId,
    templateVersion: 3,
    logo: manifest.logo || base.logo,
    logoDark: manifest.logoDark || base.logoDark,
    footerLogoBlack: manifest.footerLogoBlack || base.footerLogoBlack,
    footerLogoWhite: manifest.footerLogoWhite || base.footerLogoWhite,
    clientLogo: manifest.clientLogo || base.clientLogo,
    theme: isLegacyTheme ? { ...base.theme } : { ...base.theme, ...(manifest.theme || {}) },
  };
}
